import { FC } from "react";
import { CalendarDays } from "lucide-react";
import moment from "moment";
import CardStrip from "@/components/card-strip";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetCardSubscription,
  useGetCardSubscriptions,
  useGetDepositsInfinite,
} from "@/hooks/use-queries";
import { useRetryCardSubscriptionPayment } from "@/hooks/use-mutations";
import { useCardStyle } from "@/hooks/use-card-style";

const PAGE_SIZE = 20;

type RowState = "due" | "upcoming" | "paid" | "pending" | "failed";

type Row = {
  key: string;
  date: string;
  state: RowState;
  // Charged amount for a payment; null falls back to the plan price.
  amount: number | null;
  months: number | null;
};

const badge: Record<
  RowState,
  { label: string; color: string; background: string }
> = {
  due: { label: "Due", color: "#F87171", background: "rgba(248,113,113,0.15)" },
  upcoming: {
    label: "Upcoming",
    color: "#60A5FA",
    background: "rgba(96,165,250,0.15)",
  },
  paid: { label: "Paid", color: "#4ADE80", background: "rgba(74,222,128,0.12)" },
  pending: {
    label: "Pending",
    color: "#FACC15",
    background: "rgba(250,204,21,0.12)",
  },
  failed: {
    label: "Failed",
    color: "#F87171",
    background: "rgba(248,113,113,0.15)",
  },
};

const paymentState = (status: string): RowState =>
  status === "completed" ? "paid" : status === "failed" ? "failed" : "pending";

const SubscriptionHistory: FC<{ cardData: Card }> = ({ cardData }) => {
  const queryClient = useQueryClient();
  const cardId = cardData?.id;

  // Current billing state: gives the payment that is due and the next renewal.
  const { data: sub, isLoading: isLoadingState } = useGetCardSubscription({
    id: cardId,
  });
  // Past charges: wallet transactions of type card-subscription for this card.
  const {
    data: payments,
    isLoading: isLoadingPayments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetDepositsInfinite(
    { limit: PAGE_SIZE, type: "card-subscription", cardId },
    !!cardId,
  );
  const { data: plans } = useGetCardSubscriptions();

  const { mutate: payNow, isPending: isPaying } =
    useRetryCardSubscriptionPayment({
      id: cardId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["cardSubscription", cardId] });
        queryClient.invalidateQueries({ queryKey: ["cardInfo", cardId] });
        queryClient.invalidateQueries({ queryKey: ["cards"] });
        queryClient.invalidateQueries({ queryKey: ["deposits"] });
        queryClient.invalidateQueries({ queryKey: ["user"] });
      },
    });

  const pkg = cardData?.subscription?.package;
  const isDefault = cardData?.subscription?.isDefaultCardHolder;
  const planPrice = pkg ? (isDefault ? pkg.defaultPrice : pkg.price) : null;
  const getCardStyle = useCardStyle();
  const tier = getCardStyle(
    cardData?.bin,
    cardData?.network,
    cardData?.provider,
  ).tierLabel;
  const planLabel = `${tier ?? "Card"} (${isDefault ? "Default" : "Custom"})`;
  const currentMonths = sub?.subscribed
    ? sub.durationMonths
    : (pkg?.durationMonths ?? null);

  const rows: Row[] = [];
  if (sub?.subscribed) {
    if (sub.graceStartedAt)
      rows.push({
        key: "due",
        date: sub.graceStartedAt,
        state: "due",
        amount: null,
        months: currentMonths,
      });
    if (sub.nextBillingDate && sub.status.toLowerCase() === "active")
      rows.push({
        key: "upcoming",
        date: sub.nextBillingDate,
        state: "upcoming",
        amount: null,
        months: currentMonths,
      });
  }

  // New charges shift offset pages, so a row can repeat across pages.
  const paymentTxs: Transaction[] = [
    ...new Map(
      (payments?.pages ?? [])
        .flatMap((page: { data: Transaction[] }) => page.data)
        .map((tx: Transaction) => [tx.id, tx]),
    ).values(),
  ];
  for (const tx of paymentTxs) {
    const plan = (plans as SubscriptionPlan[] | undefined)?.find(
      (p) => p.id === tx.packageId,
    );
    rows.push({
      key: `tx-${tx.id}`,
      date: tx.createdAt,
      state: paymentState(tx.status),
      amount: Number(tx.amount),
      months: plan?.durationMonths ?? currentMonths,
    });
  }

  const isLoading = isLoadingState || isLoadingPayments;

  return (
    <div className="text-white">
      <h3 className="text-base font-semibold">Subscription History</h3>

      <div className="mt-4 rounded-2xl bg-[#EBE8F308] p-4">
        <CardStrip card={cardData} />
      </div>

      <div className="mt-4 space-y-3 pb-6">
        {isLoading ? (
          <p className="py-6 text-center text-xs text-white/60">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-white/60">
            No subscription activity yet
          </p>
        ) : (
          rows.map((row) => {
            const b = badge[row.state];
            const amount = row.amount ?? planPrice;
            return (
              <div
                key={row.key}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#EBE8F308] p-3"
              >
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/5">
                  <CalendarDays className="size-4" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {moment(row.date).format("MMM D, YYYY")}
                    </span>
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px]"
                      style={{ color: b.color, background: b.background }}
                    >
                      {b.label}
                    </span>
                  </div>
                  <p className="text-[10px] text-white/50">{planLabel}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {amount != null && (
                    <span className="text-sm font-semibold">
                      ${amount.toFixed(2)}
                    </span>
                  )}
                  {row.state === "due" ? (
                    <button
                      type="button"
                      onClick={() => payNow()}
                      disabled={isPaying}
                      className="rounded-full bg-[#E1E1E1] px-3 py-0.5 text-[10px] font-medium text-[#242424] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isPaying ? "Paying…" : "Pay Now"}
                    </button>
                  ) : (
                    <span className="text-[10px] text-white/50">
                      {row.state === "upcoming"
                        ? "Next renewal"
                        : row.months
                          ? `${row.months} month${row.months === 1 ? "" : "s"} subscription`
                          : "Subscription"}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}

        {hasNextPage && (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full rounded-xl border border-white/10 py-2 text-xs text-white/70 hover:bg-white/5 disabled:opacity-60"
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionHistory;
